import { useState, useEffect, useCallback } from 'react';
import { predictionsApi, handlePredictionError } from '../api/predictions.api';
import type { AllBasicPredictions } from '../types/predictions';

interface UseBasicPredictionsResult {
  predictions: AllBasicPredictions | null;
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

/**
 * React hook to fetch and manage BASIC predictions for a carrier
 *
 * @param dotNumber - The DOT number of the carrier
 * @param asOfDate - Optional historical date for calculations
 * @param autoFetch - Whether to automatically fetch on mount (default: true)
 * @returns Object containing predictions data, loading state, error, and refetch function
 *
 * @example
 * ```tsx
 * const { predictions, loading, error, refetch } = useBasicPredictions(123456, '2024-11-01');
 *
 * if (loading) return <LoadingSpinner />;
 * if (error) return <ErrorMessage error={error} />;
 * if (!predictions) return null;
 *
 * return (
 *   <div>
 *     {Object.entries(predictions.basics || predictions.predictions || {}).map(([basicId, prediction]) => (
 *       <BasicScoreCard key={basicId} basicId={basicId} {...prediction} />
 *     ))}
 *   </div>
 * );
 * ```
 */
export const useBasicPredictions = (
  dotNumber: number | null,
  asOfDate?: string | Date,
  autoFetch: boolean = true
): UseBasicPredictionsResult => {
  const [predictions, setPredictions] = useState<AllBasicPredictions | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const fetchPredictions = useCallback(async () => {
    if (!dotNumber) {
      setError('No DOT number provided');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const data = await predictionsApi.getAllPredictions(dotNumber, asOfDate);
      setPredictions(data);
    } catch (err) {
      const errorMessage = handlePredictionError(err);
      setError(errorMessage);
      setPredictions(null);
    } finally {
      setLoading(false);
    }
  }, [dotNumber, asOfDate]);

  useEffect(() => {
    if (autoFetch && dotNumber) {
      fetchPredictions();
    }
  }, [dotNumber, autoFetch, fetchPredictions]);

  return {
    predictions,
    loading,
    error,
    refetch: fetchPredictions,
  };
};

/**
 * Hook to fetch a single BASIC prediction
 */
export const useSingleBasicPrediction = (dotNumber: number | null, basicId: string | null) => {
  const [prediction, setPrediction] = useState<any>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const fetchPrediction = useCallback(async () => {
    if (!dotNumber || !basicId) {
      setError('DOT number and BASIC ID are required');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const data = await predictionsApi.getSinglePrediction(dotNumber, basicId);
      setPrediction(data);
    } catch (err) {
      const errorMessage = handlePredictionError(err);
      setError(errorMessage);
      setPrediction(null);
    } finally {
      setLoading(false);
    }
  }, [dotNumber, basicId]);

  useEffect(() => {
    if (dotNumber && basicId) {
      fetchPrediction();
    }
  }, [dotNumber, basicId, fetchPrediction]);

  return {
    prediction,
    loading,
    error,
    refetch: fetchPrediction,
  };
};

/**
 * Hook to fetch trajectory projection for a BASIC
 */
export const useBasicTrajectory = (
  dotNumber: number | null,
  basicId: string | null,
  months: number = 6
) => {
  const [trajectory, setTrajectory] = useState<any>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const fetchTrajectory = useCallback(async () => {
    if (!dotNumber || !basicId) {
      setError('DOT number and BASIC ID are required');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const data = await predictionsApi.getTrajectory(dotNumber, basicId, months);
      setTrajectory(data);
    } catch (err) {
      const errorMessage = handlePredictionError(err);
      setError(errorMessage);
      setTrajectory(null);
    } finally {
      setLoading(false);
    }
  }, [dotNumber, basicId, months]);

  useEffect(() => {
    if (dotNumber && basicId) {
      fetchTrajectory();
    }
  }, [dotNumber, basicId, months, fetchTrajectory]);

  return {
    trajectory,
    loading,
    error,
    refetch: fetchTrajectory,
  };
};

export default useBasicPredictions;
